import { TrackPoint, SessionStats, Segment, GPXData, AnalysisResult, SegmentStats } from '../types';

const deg2rad = (deg: number): number => {
  return deg * (Math.PI / 180);
};

// Haversine formula to calculate distance between two points in km
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const parseGPXXml = (gpxContent: string, fileName: string): GPXData => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(gpxContent, "text/xml");
  const trkpts = xmlDoc.getElementsByTagName("trkpt");

  const trackPoints: TrackPoint[] = [];
  let totalDistance = 0;
  
  for (let i = 0; i < trkpts.length; i++) {
    const pt = trkpts[i];
    const lat = parseFloat(pt.getAttribute("lat") || "0");
    const lon = parseFloat(pt.getAttribute("lon") || "0");
    const ele = parseFloat(pt.getElementsByTagName("ele")[0]?.textContent || "0");
    const timeStr = pt.getElementsByTagName("time")[0]?.textContent;
    const time = timeStr ? new Date(timeStr) : new Date();

    // Parse Heart Rate
    let hr: number | undefined = undefined;
    const extensions = pt.getElementsByTagName("extensions")[0];
    if (extensions) {
      const hrElem = 
        extensions.getElementsByTagName("gpxtpx:hr")[0] || 
        extensions.getElementsByTagName("ns3:hr")[0] ||
        extensions.getElementsByTagName("hr")[0];
        
      if (hrElem && hrElem.textContent) {
        hr = parseFloat(hrElem.textContent);
      }
    }

    let speed = 0;
    let dist = 0;

    if (i > 0) {
      const prev = trackPoints[i - 1];
      dist = calculateDistance(prev.lat, prev.lon, lat, lon);
      const timeDiffSeconds = (time.getTime() - prev.time.getTime()) / 1000;
      
      if (timeDiffSeconds > 0) {
        speed = (dist / (timeDiffSeconds / 3600));
      }
      
      // Filter huge GPS spikes (impossible speeds > 80km/h)
      if (speed > 80) speed = prev.speed;
      
      totalDistance += dist;
    }

    trackPoints.push({
      lat,
      lon,
      ele,
      time,
      speed,
      distanceFromStart: totalDistance,
      heartRate: hr
    });
  }

  // Apply Sliding Window Smoothing to Speed
  // A 5-point window helps reduce jitter that causes false segment splits
  const SMOOTHING_WINDOW = 5; 
  const smoothedSpeeds = new Array(trackPoints.length).fill(0);

  for (let i = 0; i < trackPoints.length; i++) {
    let sum = 0;
    let count = 0;
    // Window from i - 2 to i + 2
    const start = Math.max(0, i - Math.floor(SMOOTHING_WINDOW / 2));
    const end = Math.min(trackPoints.length - 1, i + Math.floor(SMOOTHING_WINDOW / 2));
    
    for (let j = start; j <= end; j++) {
      sum += trackPoints[j].speed;
      count++;
    }
    smoothedSpeeds[i] = sum / count;
  }

  // Apply smoothed speeds back to points
  trackPoints.forEach((pt, i) => {
    pt.speed = smoothedSpeeds[i];
  });

  return { trackPoints, fileName };
};

export const analyzeTrack = (trackPoints: TrackPoint[], speedThreshold: number, minDuration: number): AnalysisResult => {
  const segments: Segment[] = [];
  
  let movingTime = 0;
  let maxSpeed = 0;
  let elevationGain = 0;
  
  let totalHeartRate = 0;
  let hrCount = 0;
  let maxHeartRate = 0;

  // State for segment detection
  let currentSegmentPoints: TrackPoint[] = [];
  let pendingGap: TrackPoint[] = [];
  const GAP_TOLERANCE = 2.0; // Seconds to tolerate below threshold before cutting segment

  // Recalculate stats based on the full points array
  const totalDistance = trackPoints.length > 0 ? trackPoints[trackPoints.length - 1].distanceFromStart : 0;
  const totalDuration = trackPoints.length > 0 
    ? (trackPoints[trackPoints.length - 1].time.getTime() - trackPoints[0].time.getTime()) / 1000
    : 0;

  for (let i = 0; i < trackPoints.length; i++) {
    const pt = trackPoints[i];

    // Global Stats
    if (pt.speed > maxSpeed) maxSpeed = pt.speed;
    if (pt.heartRate) {
      totalHeartRate += pt.heartRate;
      hrCount++;
      if (pt.heartRate > maxHeartRate) maxHeartRate = pt.heartRate;
    }
    if (i > 0) {
       const prev = trackPoints[i-1];
       if (pt.ele > prev.ele && (pt.ele - prev.ele) > 0.2) {
         elevationGain += (pt.ele - prev.ele);
       }
    }

    // Segment Logic with Debouncing
    const isSpeeding = pt.speed >= speedThreshold;

    if (isSpeeding) {
      // We are moving fast enough
      if (i > 0) {
        const timeDiff = (pt.time.getTime() - trackPoints[i-1].time.getTime()) / 1000;
        movingTime += timeDiff;
      }

      // If we had a pending gap (short dip in speed), we bridge it now because speed recovered
      if (pendingGap.length > 0) {
         currentSegmentPoints.push(...pendingGap);
         pendingGap = [];
      }

      if (currentSegmentPoints.length === 0 && i > 0) {
         // Optional: include start context
      }
      currentSegmentPoints.push(pt);

    } else {
      // Speed is below threshold
      if (currentSegmentPoints.length > 0) {
        pendingGap.push(pt);
        
        const gapStartTime = pendingGap[0].time.getTime();
        const gapCurrentTime = pt.time.getTime();
        const gapDuration = (gapCurrentTime - gapStartTime) / 1000;

        if (gapDuration > GAP_TOLERANCE) {
          finalizeSegment(currentSegmentPoints, segments, minDuration);
          currentSegmentPoints = [];
          pendingGap = [];
        }
      }
    }
  }

  // Finalize last segment if active
  if (currentSegmentPoints.length > 0) {
     finalizeSegment(currentSegmentPoints, segments, minDuration);
  }

  const avgSpeed = totalDuration > 0 ? (totalDistance / (totalDuration / 3600)) : 0;
  const avgMovingSpeed = movingTime > 0 ? (totalDistance / (movingTime / 3600)) : 0;
  const avgHeartRate = hrCount > 0 ? Math.round(totalHeartRate / hrCount) : undefined;
  const efficiencyScore = Math.min(100, (avgMovingSpeed * 2) + (totalDuration / 60)); 

  return {
    segments,
    stats: {
      totalDistance,
      totalDuration,
      movingTime,
      maxSpeed,
      avgSpeed,
      avgMovingSpeed,
      elevationGain,
      efficiencyScore,
      avgHeartRate,
      maxHeartRate: maxHeartRate > 0 ? maxHeartRate : undefined
    }
  };
};

function finalizeSegment(points: TrackPoint[], segments: Segment[], minDuration: number) {
  if (points.length < 2) return;
  
  const stats = calculateSegmentStats(points);

  if (stats.duration < minDuration) return;

  segments.push({
    id: segments.length + 1,
    points: [...points],
    stats: stats
  });
}

// Reusable function to calculate stats for any array of points (used for initial analysis AND trimming)
export const calculateSegmentStats = (points: TrackPoint[]): SegmentStats => {
  if (points.length < 2) {
    return {
      distance: 0,
      duration: 0,
      avgSpeed: 0,
      maxSpeed: 0,
      startTime: points.length > 0 ? points[0].time : new Date(),
    };
  }

  const startTime = points[0].time;
  const endTime = points[points.length - 1].time;
  const duration = (endTime.getTime() - startTime.getTime()) / 1000;

  let dist = 0;
  let maxS = 0;
  let totalHR = 0;
  let hrCount = 0;
  
  for (let k = 1; k < points.length; k++) {
     dist += calculateDistance(points[k-1].lat, points[k-1].lon, points[k].lat, points[k].lon);
     if (points[k].speed > maxS) maxS = points[k].speed;
     if (points[k].heartRate) {
        totalHR += points[k].heartRate!;
        hrCount++;
     }
  }

  const avgS = duration > 0 ? (dist / (duration / 3600)) : 0;
  const avgHR = hrCount > 0 ? Math.round(totalHR / hrCount) : undefined;

  return {
    distance: dist,
    duration: duration,
    avgSpeed: avgS,
    maxSpeed: maxS,
    startTime: startTime,
    avgHeartRate: avgHR
  };
};